// Headshot crop geometry and export.
//
// Casting looks at a 4:5 portrait, so every submitted photo leaves here at
// 816x1020 (or the same ratio smaller, when the source cannot fill it — see
// exportCrop). Keeping the math here, in source-image pixels and free of any
// DOM, means the default framing rule below is testable on its own.

// What shows through wherever the photo does not cover the frame. The editor
// paints it and so does the export, so the crop on screen is the file that
// leaves — a matte the visitor cannot see coming would be a nasty surprise on
// a casting director's screen.
export const PHOTO_MATTE = "#f2f4f7";

export const PHOTO_TARGET_WIDTH = 816;
export const PHOTO_TARGET_HEIGHT = 1020;
export const PHOTO_ASPECT = PHOTO_TARGET_WIDTH / PHOTO_TARGET_HEIGHT; // 0.8

// What a browser can actually decode into a canvas, which is what the editor
// needs before it can crop. HEIC is deliberately absent: iOS converts on
// upload, and a HEIC that does reach us cannot be drawn, so accepting it would
// only produce a blank crop.
export const PHOTO_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  // Windows and some Android browsers report BMP under these instead.
  "image/x-ms-bmp",
  "image/x-bmp",
];

export const PHOTO_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".bmp"];

// The accept attribute takes both: some platforms hand over an empty or wrong
// MIME type for BMP and GIF, and the extensions keep those files selectable.
export const PHOTO_ACCEPT = PHOTO_MIME_TYPES.concat(PHOTO_EXTENSIONS).join(",");

export interface ICropRect {
  // All in SOURCE image pixels.
  x: number;
  y: number;
  width: number;
  height: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

// True when the file is something we can decode. Falls back to the extension,
// because `type` is empty for a surprising number of real files.
export const isSupportedPhoto = (file: { name?: string; type?: string }): boolean => {
  const type = (file.type || "").toLowerCase();
  if (type && PHOTO_MIME_TYPES.indexOf(type) >= 0) return true;
  const name = (file.name || "").toLowerCase();
  for (let i = 0; i < PHOTO_EXTENSIONS.length; i += 1) {
    // Match the end of the name, so "photo.png.bmp" is judged on ".bmp".
    if (name.slice(-PHOTO_EXTENSIONS[i].length) === PHOTO_EXTENSIONS[i]) return true;
  }
  return false;
};

// The largest 4:5 rectangle that fits inside the source.
export const maxCropSize = (
  srcWidth: number,
  srcHeight: number
): { width: number; height: number } => {
  if (srcWidth <= 0 || srcHeight <= 0) return { width: 0, height: 0 };
  // Source is proportionally WIDER than 4:5 → height is the binding constraint.
  if (srcWidth / srcHeight > PHOTO_ASPECT) {
    return { width: srcHeight * PHOTO_ASPECT, height: srcHeight };
  }
  return { width: srcWidth, height: srcWidth / PHOTO_ASPECT };
};

// Where the frame sits before the user touches anything.
//
// Horizontally: centred. Faces are centred in almost every photo anyone takes.
//
// Vertically: NOT centred — this is the whole point. Centring a 4:5 window on a
// portrait photo slices the top of the head off, and hair is the first thing
// the person notices. So the window is placed with its centre one third of the
// way down the image, which frames the TOP TWO THIRDS: on a 9:16 phone photo it
// lands flush against the top edge, on a 3:4 photo it is top-aligned too, and
// its centre is never lower than a third of the way down whatever it is given.
export const defaultCrop = (srcWidth: number, srcHeight: number): ICropRect => {
  const size = maxCropSize(srcWidth, srcHeight);
  const x = (srcWidth - size.width) / 2;
  const y = clamp(srcHeight / 3 - size.height / 2, 0, srcHeight - size.height);
  return { x, y, width: size.width, height: size.height };
};

// ---------------------------------------------------------------------------
// Auto-framing
//
// A photo dropped on the page is framed without asking. The rule below is a
// small heuristic, not face detection: the image is sampled down to a thumbnail
// and scored per row and per column, then the frame is placed on what the score
// says is the subject. Two signals, because either one alone is fooled easily:
//
//   skin   — the classic RGB skin-tone test. Strong evidence of a face, and
//            wrong often enough (wood, sand, terracotta) that it is weighted
//            rather than trusted outright.
//   detail — local luma gradient. A face carries far more of it than a wall or
//            a sky, so it finds the subject even when the skin test says
//            nothing (heavy shadow, black-and-white, an unusual white balance).
//
// Everything here is deliberately cheap: one 64px-wide draw and a single pass
// over ~4000 pixels, so it costs nothing on a phone. When it finds nothing to
// go on it hands back defaultCrop, which is the geometric rule and never worse
// than what the visitor had before.
// ---------------------------------------------------------------------------

// The long edge of the thumbnail the analysis runs on. Big enough to locate a
// head, small enough that the whole pass is a fraction of a frame.
const ANALYSIS_SIZE = 64;

// Hair, hats and foreheads sit above the first skin the scan finds, so the
// frame opens this far above it. A share of the frame's height, not a pixel
// count, so it holds for any photo size.
const HEADROOM = 0.12;

// How much of the subject mass has to accumulate before a row counts as "the
// subject starts here". Low, because it is looking for the top edge — but not
// zero, or one stray warm pixel decides the framing.
const SUBJECT_TOP_SHARE = 0.06;

export interface IPhotoAnalysis {
  // Grid the scores were measured on, in its own pixels.
  width: number;
  height: number;
  // Combined subject score per column / per row.
  columns: number[];
  rows: number[];
  total: number;
  // Skin alone, which is what locates the top of a head.
  skinRows: number[];
  skinColumns: number[];
  skinTotal: number;
}

// The well-worn RGB skin rule (Kovac et al). Cheap, and good enough to say
// "there is probably a face around here" when it agrees with the detail signal.
const isSkin = (r: number, g: number, b: number): boolean => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return (
    r > 95 &&
    g > 40 &&
    b > 20 &&
    max - min > 15 &&
    Math.abs(r - g) > 15 &&
    r > g &&
    r > b
  );
};

// Sample the image down and score it. Returns null when the canvas cannot be
// read — a cross-origin source taints it, and some privacy modes refuse
// getImageData outright. Callers fall back to the geometric rule.
export const analysePhoto = (image: HTMLImageElement): IPhotoAnalysis | null => {
  const srcWidth = image.naturalWidth || 0;
  const srcHeight = image.naturalHeight || 0;
  if (srcWidth <= 0 || srcHeight <= 0) return null;

  const scale = Math.min(1, ANALYSIS_SIZE / Math.max(srcWidth, srcHeight));
  const width = Math.max(1, Math.round(srcWidth * scale));
  const height = Math.max(1, Math.round(srcHeight * scale));

  let data: Uint8ClampedArray;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, width, height);
    data = ctx.getImageData(0, 0, width, height).data;
  } catch (e) {
    return null;
  }

  const columns: number[] = [];
  const rows: number[] = [];
  const skinColumns: number[] = [];
  const skinRows: number[] = [];
  for (let i = 0; i < width; i += 1) {
    columns.push(0);
    skinColumns.push(0);
  }
  for (let i = 0; i < height; i += 1) {
    rows.push(0);
    skinRows.push(0);
  }

  const luma = new Float32Array(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const at = (y * width + x) * 4;
      const r = data[at];
      const g = data[at + 1];
      const b = data[at + 2];
      luma[y * width + x] = 0.299 * r + 0.587 * g + 0.114 * b;
      if (isSkin(r, g, b)) {
        skinColumns[x] += 1;
        skinRows[y] += 1;
      }
    }
  }

  let skinTotal = 0;
  for (let i = 0; i < skinRows.length; i += 1) skinTotal += skinRows[i];

  // Detail: how much this pixel differs from the one left of it and the one
  // above. Normalised to 0..1 so the weighting below means something.
  let total = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const here = luma[y * width + x];
      const dx = x > 0 ? Math.abs(here - luma[y * width + x - 1]) : 0;
      const dy = y > 0 ? Math.abs(here - luma[(y - 1) * width + x]) : 0;
      const detail = Math.min(1, (dx + dy) / 128);
      const at = (y * width + x) * 4;
      const skin = isSkin(data[at], data[at + 1], data[at + 2]) ? 1 : 0;
      // Skin counts for more, but cannot win on its own against a face's
      // detail — a large flat expanse of skin-coloured wall scores low.
      const score = skin * 3 + detail;
      columns[x] += score;
      rows[y] += score;
      total += score;
    }
  }

  return { width, height, columns, rows, total, skinRows, skinColumns, skinTotal };
};

const centroid = (buckets: number[], sum: number): number => {
  if (sum <= 0) return -1;
  let weighted = 0;
  for (let i = 0; i < buckets.length; i += 1) weighted += (i + 0.5) * buckets[i];
  return weighted / sum;
};

// The first bucket by which `share` of the mass has accumulated.
const leadingEdge = (buckets: number[], sum: number, share: number): number => {
  if (sum <= 0) return -1;
  const target = sum * share;
  let running = 0;
  for (let i = 0; i < buckets.length; i += 1) {
    running += buckets[i];
    if (running >= target) return i;
  }
  return -1;
};

// Place the frame from an analysis. Pure, so the rule is testable without a
// canvas: hand it scores and check where the frame lands.
export const autoFrame = (
  analysis: IPhotoAnalysis | null,
  srcWidth: number,
  srcHeight: number
): ICropRect => {
  const fallback = defaultCrop(srcWidth, srcHeight);
  if (!analysis || !analysis.total || !analysis.width || !analysis.height) {
    return fallback;
  }
  const size = maxCropSize(srcWidth, srcHeight);
  if (!size.width) return fallback;

  const scaleX = srcWidth / analysis.width;
  const scaleY = srcHeight / analysis.height;

  // Horizontal: centre on the subject's centroid. Uses the combined score —
  // for side-on or off-centre framing, detail tracks the subject as well as
  // skin does, and averaging the two is steadier than either.
  const cx = centroid(analysis.columns, analysis.total);
  const skinCx = centroid(analysis.skinColumns, analysis.skinTotal);
  const centreX =
    skinCx >= 0 ? ((cx + skinCx) / 2) * scaleX : cx >= 0 ? cx * scaleX : srcWidth / 2;

  // Vertical: open the frame a little above where the subject starts, so hair
  // is inside it. Skin finds that edge best; the combined score is the backup
  // for a photo where the skin test says nothing at all.
  const topBucket =
    analysis.skinTotal > 0
      ? leadingEdge(analysis.skinRows, analysis.skinTotal, SUBJECT_TOP_SHARE)
      : leadingEdge(analysis.rows, analysis.total, SUBJECT_TOP_SHARE);

  const y =
    topBucket >= 0
      ? clamp(
          topBucket * scaleY - size.height * HEADROOM,
          0,
          srcHeight - size.height
        )
      : fallback.y;

  return {
    x: clamp(centreX - size.width / 2, 0, srcWidth - size.width),
    y,
    width: size.width,
    height: size.height,
  };
};

// Analyse and frame in one call, with the geometric rule as the safety net.
export const autoFrameFor = (image: HTMLImageElement): ICropRect => {
  const width = image.naturalWidth || 0;
  const height = image.naturalHeight || 0;
  try {
    return autoFrame(analysePhoto(image), width, height);
  } catch (e) {
    return defaultCrop(width, height);
  }
};

// Load a file URL into an image, so callers can analyse or export without
// mounting anything.
export const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        reject(new Error("emptyImage"));
        return;
      }
      resolve(img);
    };
    img.onerror = () => reject(new Error("decodeFailed"));
    img.src = url;
  });

// The zoom that a given frame corresponds to, so the editor can open on a crop
// somebody else chose (the auto-frame) rather than resetting to fill.
export const zoomForCrop = (
  crop: ICropRect,
  srcWidth: number,
  srcHeight: number
): number => {
  const fill = maxCropSize(srcWidth, srcHeight);
  if (!fill.width || !crop.width) return 1;
  return fill.width / crop.width;
};

// The zoom at which the WHOLE photo fits inside the frame. Below 1, because 1
// is "fill the frame" — at contain zoom the frame is bigger than the photo on
// one axis and the matte shows.
//
// This is what gives a landscape photo any vertical movement at all. At fill
// zoom a 4:5 window on a landscape source is exactly as tall as the source, so
// there is nothing above or below to reveal and dragging up or down does
// nothing. Zooming out past the fill gives the photo room to move.
export const containZoom = (srcWidth: number, srcHeight: number): number => {
  const fill = maxCropSize(srcWidth, srcHeight);
  if (!fill.width) return 1;
  const containWidth =
    srcWidth / srcHeight > PHOTO_ASPECT ? srcWidth : srcHeight * PHOTO_ASPECT;
  return fill.width / containWidth;
};

// Keep the photo and the frame overlapping after a drag or a zoom.
//
// Two cases per axis, and they are mirror images:
//   frame smaller than the photo → the frame slides within the photo
//   frame larger than the photo  → the photo slides within the frame
// Either way the range is the slack between them, so both axes have somewhere
// to go whenever the two differ, and the photo can never be dragged out of
// frame entirely.
export const clampCrop = (
  crop: ICropRect,
  srcWidth: number,
  srcHeight: number
): ICropRect => {
  const spanX = srcWidth - crop.width;
  const spanY = srcHeight - crop.height;
  return {
    width: crop.width,
    height: crop.height,
    x: spanX >= 0 ? clamp(crop.x, 0, spanX) : clamp(crop.x, spanX, 0),
    y: spanY >= 0 ? clamp(crop.y, 0, spanY) : clamp(crop.y, spanY, 0),
  };
};

// A frame of the given zoom (1 = the whole default frame) centred on a point.
// The aspect ratio is derived, never passed in, so no caller can produce a
// frame that is not 4:5.
export const cropAt = (
  srcWidth: number,
  srcHeight: number,
  zoom: number,
  centreX: number,
  centreY: number
): ICropRect => {
  const base = maxCropSize(srcWidth, srcHeight);
  // Zoom floor is "the whole photo fits", not "the frame is full" — see
  // containZoom. Anything below that is empty matte and nothing else.
  //
  // Note `isFinite` rather than `zoom || 1`: the falsy check would turn a
  // requested zoom of 0 into FILL, which is the opposite end of the range from
  // where the caller was heading. Only a missing or non-numeric zoom defaults.
  const requested = typeof zoom === "number" && isFinite(zoom) ? zoom : 1;
  const safeZoom = Math.max(containZoom(srcWidth, srcHeight), requested);
  const width = base.width / safeZoom;
  const height = width / PHOTO_ASPECT;
  return clampCrop(
    { x: centreX - width / 2, y: centreY - height / 2, width, height },
    srcWidth,
    srcHeight
  );
};

// Output size for a given frame: the target, or the frame's own size when the
// source cannot fill it. Upscaling a small photo to 816 wide only makes a
// blurrier file of the same picture, so the ratio is honoured and the pixels
// are not invented.
export const outputSize = (crop: ICropRect): { width: number; height: number } => {
  if (crop.width >= PHOTO_TARGET_WIDTH) {
    return { width: PHOTO_TARGET_WIDTH, height: PHOTO_TARGET_HEIGHT };
  }
  const width = Math.max(1, Math.round(crop.width));
  return { width, height: Math.round(width / PHOTO_ASPECT) };
};

// Draw the frame out to a JPEG File.
//
// The matte is painted first, for two reasons: JPEG has no alpha, so a PNG or
// GIF with transparency would otherwise come out with black behind it; and the
// frame can be larger than the photo once the visitor zooms out, so part of the
// output may be matte by their own choice.
export const exportCrop = (
  image: HTMLImageElement,
  crop: ICropRect,
  fileName: string
): Promise<File> =>
  new Promise((resolve, reject) => {
    const size = outputSize(crop);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("canvasUnavailable"));
      return;
    }
    // The matte first: the frame can be larger than the photo now, and JPEG has
    // no alpha, so whatever is not covered has to be painted with something.
    ctx.fillStyle = PHOTO_MATTE;
    ctx.fillRect(0, 0, size.width, size.height);

    // Intersect the frame with the photo and map that overlap to the same place
    // in the output. drawImage does clip out-of-bounds source rectangles per
    // spec, but doing it here is explicit and does not rely on every browser
    // agreeing about the edge case.
    const scale = size.width / crop.width;
    const sx = Math.max(0, crop.x);
    const sy = Math.max(0, crop.y);
    const sw = Math.min(image.naturalWidth || 0, crop.x + crop.width) - sx;
    const sh = Math.min(image.naturalHeight || 0, crop.y + crop.height) - sy;
    if (sw > 0 && sh > 0) {
      ctx.drawImage(
        image,
        sx,
        sy,
        sw,
        sh,
        (sx - crop.x) * scale,
        (sy - crop.y) * scale,
        sw * scale,
        sh * scale
      );
    }

    const finish = (blob: Blob | null) => {
      if (!blob) {
        reject(new Error("encodeFailed"));
        return;
      }
      // File is not constructible in every browser this app still supports, so
      // fall back to tagging the Blob with the fields the upload reads.
      try {
        resolve(new File([blob], fileName, { type: "image/jpeg" }));
      } catch (e) {
        const asFile: any = blob;
        asFile.name = fileName;
        asFile.lastModified = 0;
        resolve(asFile as File);
      }
    };

    if (canvas.toBlob) {
      canvas.toBlob(finish, "image/jpeg", 0.92);
      return;
    }
    // Safari 10 and friends: hand-roll the blob from a data URL.
    try {
      const parts = canvas.toDataURL("image/jpeg", 0.92).split(",");
      const binary = atob(parts[1]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      finish(new Blob([bytes], { type: "image/jpeg" }));
    } catch (e) {
      reject(new Error("encodeFailed"));
    }
  });

// "IMG_4821.bmp" -> "IMG_4821.jpg". The editor always emits JPEG, so the
// extension has to change; keeping the stem means the file the visitor picked
// is still recognisable in the confirmation.
export const croppedFileName = (originalName?: string): string => {
  const stem = (originalName || "").replace(/\.[^.]*$/, "").trim();
  return `${stem || "headshot"}.jpg`;
};
